import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, X, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { toast } from 'react-hot-toast';

import { apiClient } from '../../lib/api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useJobs } from '../../contexts/JobContext';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ComprehensiveCandidateForm } from './ComprehensiveCandidateForm';

interface CVUploadModalProps {
  onSuccess: () => void;
  onClose: () => void;
  showComprehensiveForm?: boolean;
}

interface UploadStatus {
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error';
  message: string;
  progress?: number;
}

export function CVUploadModal({ onSuccess, onClose, showComprehensiveForm = false }: CVUploadModalProps) {
  const { user } = useAuth();
  const { addLocal } = useJobs();
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    status: 'idle',
    message: 'Select or drag a CV file to upload'
  });
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [, setCandidateId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [parsedData, setParsedData] = useState<any>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      // Additional client-side validation for file extension
      const fileName = file.name.toLowerCase();
      const allowedExtensions = ['.pdf', '.docx'];
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error('Only PDF and DOCX files are allowed');
        return;
      }
      
      setUploadedFile(file);
      setUploadStatus({
        status: 'idle',
        message: `Selected: ${file.name}`
      });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
    onDropRejected: (fileRejections: any[]) => {
      const rejection = fileRejections[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        toast.error('File size cannot exceed 10MB');
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        toast.error('Only PDF and DOCX files are allowed');
      } else {
        toast.error('File upload failed');
      }
    }
  });

  const handleUpload = async () => {
    if (!uploadedFile) return;

    setUploadStatus({
      status: 'uploading',
      message: 'Uploading resume...',
      progress: 0
    });

    try {
      // Start a resumable job ticket
      const start = await apiClient.jobsStart({ user_id: user?.id || 'me', type: 'cv_upload', metadata: { file_name: uploadedFile.name } });
      const jobId = start.job.id;
      addLocal(jobId);

      // Upload resume using new API
      const response = await apiClient.uploadResume(uploadedFile);
      
      setCandidateId(response.candidate_id);
      setUploadStatus({
        status: 'processing',
        message: 'Resume uploaded successfully! Processing with AI...'
      });

      // Update job anchors and resume URL, then start polling
      await apiClient.jobsUpdate(jobId, { status: 'processing', anchors: { candidate_id: response.candidate_id }, resume_url: `/candidates/${response.candidate_id}` });
      // Start polling for processing status
      pollProcessingStatus(response.candidate_id, jobId);

    } catch (error: any) {
      setUploadStatus({
        status: 'error',
        message: error.response?.data?.detail || 'Upload failed'
      });
      toast.error('Failed to upload resume');
    }
  };

  const pollProcessingStatus = async (candidateId: string, jobId?: string) => {
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    let attempts = 0;
    let isPolling = true;

    const poll = async () => {
      if (!isPolling) return;
      
      try {
        console.log(`🔍 Polling resume status for candidate ${candidateId}, attempt ${attempts + 1}/${maxAttempts}`);
        const statusResponse = await apiClient.getResumeProcessingStatus(candidateId);
        console.log(`📊 Resume Status response:`, statusResponse);
        
        if (statusResponse.status === 'completed') {
          console.log('✅ Resume processing completed, stopping polling');
          isPolling = false;
          setUploadStatus({
            status: 'completed',
            message: 'Resume processing completed successfully!',
            progress: 100
          });
          
          // Store parsed data for form
          if (statusResponse.parsed_fields) {
            setParsedData(statusResponse.parsed_fields);
          }
          
          toast.success('Resume processing completed successfully!');
          
          // Mark job complete
          try { if (jobId) await apiClient.jobsUpdate(jobId, { status: 'completed', result_url: `/candidates/${candidateId}` }); } catch {}
          // Show comprehensive form if enabled
          if (showComprehensiveForm) {
            setShowForm(true);
          } else {
            setTimeout(() => {
              onSuccess();
            }, 2000);
          }
          return; // Exit polling
        } else if (statusResponse.status === 'failed') {
          console.log('❌ Resume processing failed, stopping polling');
          isPolling = false;
          setUploadStatus({
            status: 'error',
            message: statusResponse.message || 'Resume processing failed. Please try again.',
            progress: 0
          });
          toast.error('Resume processing failed. Please try again.');
          return; // Exit polling
        } else if (attempts < maxAttempts) {
          // Still processing, continue polling with progress update
          attempts++;
          const progress = Math.min(20 + (attempts * 2), 90); // Gradual progress from 20% to 90%
          console.log(`🔄 Still processing, attempt ${attempts}/${maxAttempts}, progress: ${progress}%`);
          setUploadStatus(prev => ({
            ...prev,
            message: `Processing resume... (${attempts}/${maxAttempts})`,
            progress
          }));
          
          if (isPolling) {
            setTimeout(poll, 10000); // Poll every 10 seconds
          }
        } else {
          // Timeout
          console.log('⏰ Polling timeout reached');
          isPolling = false;
          setUploadStatus({
            status: 'error',
            message: 'Processing timeout. Please check back later.',
            progress: 0
          });
          toast.error('Processing timeout. Please check back later.');
        }
      } catch (error) {
        console.error('❌ Error polling resume status:', error);
        if (attempts < maxAttempts && isPolling) {
          attempts++;
          setTimeout(poll, 10000);
        } else {
          isPolling = false;
          setUploadStatus({
            status: 'error',
            message: 'Failed to check processing status',
            progress: 0
          });
          toast.error('Failed to check processing status');
        }
      }
    };

    // Start polling after a short delay
    console.log(`🚀 Starting CV processing status polling for candidate ${candidateId}`);
    setTimeout(poll, 5000);
  };

  const handleClose = () => {
    if (uploadStatus.status === 'uploading' || uploadStatus.status === 'processing') {
      toast.error('Please wait for the upload to complete');
      return;
    }
    onClose();
  };

  const getStatusIcon = () => {
    switch (uploadStatus.status) {
      case 'uploading':
        return <Loader className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'processing':
        return <Loader className="w-5 h-5 text-yellow-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Upload className="w-5 h-5 text-foreground-400" />;
    }
  };

  const getStatusColor = () => {
    switch (uploadStatus.status) {
      case 'uploading':
        return 'text-blue-600';
      case 'processing':
        return 'text-yellow-600';
      case 'completed':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-foreground-600';
    }
  };

  // Show comprehensive form if enabled and processing is complete
  if (showComprehensiveForm && showForm && parsedData) {
    return (
      <ComprehensiveCandidateForm
        onSuccess={() => {
          onSuccess();
          onClose();
        }}
        onCancel={() => {
          setShowForm(false);
          onClose();
        }}
        editData={parsedData}
        mode="create"
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-foreground-900">
                Upload CV
              </h2>
              <p className="text-sm text-foreground-600 mt-1">
                Upload a candidate's CV for AI-powered parsing
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="text-foreground-400 hover:text-foreground-600"
              disabled={uploadStatus.status === 'uploading' || uploadStatus.status === 'processing'}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Upload Area */}
          {!uploadedFile && (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-foreground-300 hover:border-primary-400 hover:bg-primary-50'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="w-12 h-12 text-foreground-400 mx-auto mb-4" />
              <p className="text-foreground-600 mb-2">
                {isDragActive ? 'Drop the CV file here' : 'Drag & drop a CV file here'}
              </p>
              <p className="text-sm text-foreground-500">
                or click to select a file
              </p>
              <p className="text-xs text-foreground-400 mt-2">
                Supports PDF and DOCX only (max 10MB)
              </p>
            </div>
          )}

          {/* Selected File */}
          {uploadedFile && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <File className="w-8 h-8 text-blue-600" />
                <div className="flex-1">
                  <p className="font-medium text-foreground-900">{uploadedFile.name}</p>
                  <p className="text-sm text-foreground-500">
                    {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                {uploadStatus.status === 'idle' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setUploadedFile(null);
                      setUploadStatus({
                        status: 'idle',
                        message: 'Select or drag a CV file to upload'
                      });
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Status Display */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {getStatusIcon()}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${getStatusColor()}`}>
                    {uploadStatus.message}
                  </p>
                  {uploadStatus.progress !== undefined && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadStatus.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Upload Button */}
              {uploadStatus.status === 'idle' && (
                <Button
                  onClick={handleUpload}
                  className="w-full"
                >
                  Upload CV
                </Button>
              )}

              {/* Processing Info */}
              {uploadStatus.status === 'processing' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-yellow-700">
                    <Loader className="w-4 h-4 animate-spin" />
                    <span className="text-sm font-medium">AI Processing</span>
                  </div>
                  <p className="text-yellow-600 text-sm mt-1">
                    Our AI is analyzing the CV to extract skills, experience, and other details. 
                    This may take a few minutes.
                  </p>
                </div>
              )}

              {/* Success Message */}
              {uploadStatus.status === 'completed' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Processing Complete</span>
                  </div>
                  <p className="text-green-600 text-sm mt-1">
                    CV has been successfully processed and candidate profile has been updated.
                  </p>
                </div>
              )}

              {/* Error Message */}
              {uploadStatus.status === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Upload Failed</span>
                  </div>
                  <p className="text-red-600 text-sm mt-1">
                    {uploadStatus.message}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={uploadStatus.status === 'uploading' || uploadStatus.status === 'processing'}
            >
              {uploadStatus.status === 'completed' ? 'Done' : 'Cancel'}
            </Button>
            {uploadStatus.status === 'error' && (
              <Button
                onClick={() => {
                  setUploadedFile(null);
                  setUploadStatus({
                    status: 'idle',
                    message: 'Select or drag a CV file to upload'
                  });
                }}
                className="flex-1"
              >
                Try Again
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
